"use client"

import Image from "next/image"
import Link from "next/link"
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock,
  ShoppingBag,
  Tag,
} from "lucide-react"
import { BlogPost } from "@lib/data/blog"
import { StoreProduct } from "@/types/store"
import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { useTranslation } from "react-i18next"

type BlogPostTemplateProps = {
  post: BlogPost
  relatedPosts: BlogPost[]
  linkedProducts?: StoreProduct[]
}

const S3_IMAGE_BASE_URL =
  process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL ||
  "https://101yapi-storage.s3.eu-west-2.amazonaws.com/"

const resolveImageUrl = (imageKey?: string | null) => {
  if (!imageKey) return null
  if (imageKey.startsWith("http") || imageKey.startsWith("/")) return imageKey

  return `${S3_IMAGE_BASE_URL.replace(/\/$/, "")}/${imageKey.replace(/^\//, "")}`
}

const getProductImage = (product: StoreProduct) => {
  const productImage =
    product.images?.find((image) => image.is_thumbnail === 1) ||
    product.images?.[0]

  if (productImage?.image_key) {
    return resolveImageUrl(productImage.image_key)
  }

  const variantImage = product.variants
    ?.flatMap((variant) => variant.images || [])
    .find((image) => image.is_thumbnail === 1)

  return resolveImageUrl(variantImage?.image_key)
}

const formatPrice = (price?: number | null) => {
  if (!price) return null

  return price.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
  })
}

export default function BlogPostTemplate({
  post,
  relatedPosts,
  linkedProducts = [],
}: BlogPostTemplateProps) {
  const { t } = useTranslation()

  return (
    <main className="bg-slate-50">
      <article>
        <header className="border-b border-orange-100 bg-white">
          <div className="content-container px-4 py-6 small:px-6 lg:py-8">
            <Link
              href="/blog"
              className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-orange-700"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("blog.backToBlog")}
            </Link>
            <div className="grid gap-8 lg:grid-cols-[1fr,420px] lg:items-center">
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                  <span className="rounded-full bg-orange-100 px-3 py-1 font-semibold text-orange-700">
                    {post.category.title}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-4 w-4" />
                    {post.publishedAt}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {t("blog.readTime", { count: post.readTimeMinutes })}
                  </span>
                </div>
                <h1 className="max-w-4xl text-4xl font-black tracking-tight text-slate-950 lg:text-5xl">
                  {post.title}
                </h1>
                <p className="max-w-3xl text-lg leading-8 text-slate-600">
                  {post.excerpt}
                </p>
                <p className="text-sm font-semibold text-slate-500">
                  {t("blog.author")}: {post.authorName}
                </p>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden rounded-lg border border-slate-200">
                <Image
                  src={post.coverImage}
                  alt={post.coverAlt}
                  fill
                  className="object-cover"
                  priority
                  unoptimized
                />
              </div>
            </div>
          </div>
        </header>

        <div className="content-container grid gap-8 px-4 py-8 small:px-6 lg:grid-cols-[minmax(0,1fr),320px] lg:py-10">
          <div className="space-y-8 rounded-lg border border-slate-200 bg-white p-6 lg:p-8">
            {post.sections.map((section) => (
              <section key={section.heading} className="space-y-4">
                <h2 className="text-2xl font-bold tracking-tight text-slate-950">
                  {section.heading === "İçerik"
                    ? t("blog.content")
                    : section.heading}
                </h2>
                {section.body.map((paragraph) => (
                  <div
                    key={paragraph}
                    className="max-w-none text-slate-700 [&_a]:font-semibold [&_a]:text-orange-700 [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-4 [&_blockquote]:border-orange-200 [&_blockquote]:pl-4 [&_blockquote]:text-slate-600 [&_br]:block [&_h1]:mb-4 [&_h1]:mt-8 [&_h1]:text-3xl [&_h1]:font-black [&_h1]:tracking-tight [&_h1]:text-slate-950 [&_h2]:mb-3 [&_h2]:mt-7 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-slate-950 [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-950 [&_li]:my-2 [&_li>p]:my-0 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_p]:my-4 [&_p]:text-base [&_p]:leading-8 [&_p]:text-slate-700 [&_strong]:font-bold [&_strong]:text-slate-950 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
              </section>
            ))}
          </div>

          <aside className="space-y-5">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="mb-3 text-lg font-bold text-slate-950">
                {t("blog.relatedProducts")}
              </h2>
              <div className="space-y-2">
                {post.linkedProducts.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:text-orange-700"
                  >
                    {item.title}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="mb-3 text-lg font-bold text-slate-950">
                {t("blog.relatedCategories")}
              </h2>
              <div className="space-y-2">
                {post.linkedCategories.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:text-orange-700"
                  >
                    {item.title}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-orange-100 bg-orange-50 p-5">
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-slate-950">
                <Tag className="h-5 w-5 text-orange-600" />
                {t("blog.tags")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      {linkedProducts.length > 0 && (
        <section className="content-container px-4 pb-12 small:px-6">
          <div className="relative overflow-hidden rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/95 via-white to-slate-50 p-6 shadow-sm ring-1 ring-orange-100/60 sm:p-8 lg:p-10">
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-orange-200/25 blur-3xl"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-slate-200/30 blur-3xl"
              aria-hidden
            />

            <div className="relative">
              <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-2xl">
                  <p className="mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-700">
                    <ShoppingBag className="h-4 w-4" aria-hidden />
                    {t("blog.recommendedWithArticle")}
                  </p>
                  <h2 className="text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">
                    {t("blog.linkedProducts")}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                    {t("blog.linkedProductsDescription")}
                  </p>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                {linkedProducts.map((product) => {
                  const productImage = getProductImage(product)
                  const primaryVariant = product.variants?.[0]
                  const salesPrice = primaryVariant?.sales_price ?? null
                  const marketPrice = primaryVariant?.market_price ?? null
                  const formattedSalesPrice = formatPrice(salesPrice)
                  const formattedMarketPrice = formatPrice(marketPrice)
                  const hasDiscount =
                    marketPrice !== null &&
                    salesPrice !== null &&
                    marketPrice > salesPrice
                  const discountPercent =
                    hasDiscount && marketPrice && salesPrice
                      ? Math.round((1 - salesPrice / marketPrice) * 100)
                      : null

                  return (
                    <Link
                      key={product.id}
                      href={`/products/${product.handle}`}
                      className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100/80 transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-lg hover:ring-orange-100"
                    >
                      <div className="relative aspect-[4/5] bg-gradient-to-b from-slate-50 to-white p-3">
                        {hasDiscount && discountPercent !== null ? (
                          <span className="absolute left-3 top-3 z-10 rounded-full bg-orange-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
                            {t("blog.discountPercent", {
                              percent: discountPercent,
                            })}
                          </span>
                        ) : null}
                        {productImage ? (
                          <Image
                            src={productImage}
                            alt={product.title || t("product.productImage")}
                            fill
                            className="object-contain object-center p-1 transition duration-300 group-hover:scale-[1.02]"
                            unoptimized
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-sm font-semibold text-slate-400">
                            {t("common.noImage")}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col gap-3 p-4 pt-3">
                        <h3 className="line-clamp-2 min-h-[3rem] text-base font-bold leading-snug text-slate-950 sm:text-lg">
                          {normalizeTextEncoding(product.title || "")}
                        </h3>

                        <div className="mt-auto space-y-3">
                          <div className="flex min-h-[1.75rem] flex-wrap items-end gap-2">
                            {formattedMarketPrice &&
                              formattedSalesPrice &&
                              hasDiscount && (
                                <span className="text-sm font-semibold text-slate-400 line-through">
                                  {formattedMarketPrice}
                                </span>
                              )}
                            {formattedSalesPrice ? (
                              <span className="text-xl font-black text-slate-950">
                                {formattedSalesPrice}
                              </span>
                            ) : (
                              <span className="text-sm font-semibold text-slate-500">
                                {t("blog.noPrice")}
                              </span>
                            )}
                          </div>

                          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-700 transition group-hover:gap-2">
                            {t("blog.goToProduct")}
                            <ArrowRight className="h-4 w-4 shrink-0 transition group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {relatedPosts.length > 0 && (
        <section className="content-container px-4 pb-10 small:px-6">
          <h2 className="mb-4 text-2xl font-bold text-slate-950">
            {t("blog.relatedPosts")}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.slug}
                href={`/blog/${relatedPost.slug}`}
                className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:border-orange-200 hover:shadow-md"
              >
                <p className="mb-2 text-xs font-semibold uppercase text-orange-700">
                  {relatedPost.category.title}
                </p>
                <h3 className="text-lg font-bold text-slate-950">
                  {relatedPost.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">
                  {relatedPost.excerpt}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  )
}
